import React, { useEffect, useRef, useState } from "react";
import showroom from "../Assets/showroom/showromm.png";
import { FaAward, FaTools, FaCreditCard, FaShippingFast, FaArrowRight } from "react-icons/fa";

const features = [
  { icon: <FaAward />, label: "Trusted Dealer", sub: "Authorised & certified" },
  { icon: <FaTools />, label: "Expert Support", sub: "Factory-trained team" },
  { icon: <FaCreditCard />, label: "Easy Finance", sub: "Flexible EMI plans" },
  { icon: <FaShippingFast />, label: "Fast Delivery", sub: "Same-day processing" },
];

const stats = [
  { num: "500+", label: "Happy Riders" },
  { num: "20+", label: "Years of Service" },
  { num: "50+", label: "Bike Models" },
];

const About = ({ onOpenModal }) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        /* ── FONT CHANGE: Oswald (display) + DM Sans (body) ── */
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=DM+Sans:wght@400;500;600&family=Barlow+Condensed:wght@400;600;700;800&display=swap');

        .font-bebas       { font-family: 'Oswald', sans-serif; letter-spacing: 0.02em; }
        .font-barlow      { font-family: 'DM Sans', sans-serif; }
        .font-barlow-cond { font-family: 'Barlow Condensed', sans-serif; }

        /* Ticker */
        .ticker-track {
          display: flex; gap: 80px; white-space: nowrap;
          animation: tickerScroll 20s linear infinite;
        }
        @keyframes tickerScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        /* Corner brackets */
        .corner-bracket {
          position: absolute; width: 28px; height: 28px;
          border-color: #D4AF37; border-style: solid;
        }
        .tl { top:-2px; left:-2px;  border-width: 2px 0 0 2px; }
        .tr { top:-2px; right:-2px; border-width: 2px 2px 0 0; }
        .bl { bottom:-2px; left:-2px;  border-width: 0 0 2px 2px; }
        .br { bottom:-2px; right:-2px; border-width: 0 2px 2px 0; }

        /* Hex badge */
        .hex-badge {
          clip-path: polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);
        }

        /* Feature card */
        .feat-card {
          border-left: 3px solid #D4AF37;
          transition: background 0.3s, transform 0.3s;
        }
        .feat-card:hover {
          background: rgba(212,175,55,0.07) !important;
          transform: translateX(4px);
        }

        /* Ghost watermark */
        .about-watermark {
          font-family: 'Oswald', sans-serif;
          font-size: clamp(80px, 16vw, 200px);
          color: rgba(212,175,55,0.04);
          letter-spacing: -4px;
          pointer-events: none;
          user-select: none;
          white-space: nowrap;
        }

        /* Scroll reveal */
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .reveal.shown {
          opacity: 1;
          transform: translateY(0);
        }
        .reveal.delay-100 { transition-delay: 0.1s; }
        .reveal.delay-200 { transition-delay: 0.2s; }
        .reveal.delay-300 { transition-delay: 0.3s; }
        .reveal.delay-400 { transition-delay: 0.4s; }

        /* CTA button */
        .btn-slash {
          clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%);
        }

        /* Showroom img overlay */
        .img-overlay {
          background: linear-gradient(135deg, rgba(212,175,55,0.1) 0%, transparent 60%);
          mix-blend-mode: screen;
        }

        /* Stat line */
        .stat-bar {
          background: linear-gradient(to right, #D4AF37, transparent);
        }
      `}</style>

      {/* ── TICKER ── */}
      <div className="bg-[#111111] border-y border-[#D4AF37]/12 py-3 overflow-hidden mt-5">
        <div className="ticker-track">
          {[...Array(2)].flatMap(() =>
            ["Premium Motorcycles", "Test Ride Available", "Easy Finance Options", "Expert Service", "Authorised Dealer", "Katpadi · Tamil Nadu"].map((t, i) => (
              <div key={`${t}-${i}`} className="flex items-center gap-5 flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                <span className="font-barlow-cond text-[12px] tracking-[4px] uppercase text-[#666]">{t}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── ABOUT SECTION ── */}
      <section
        ref={sectionRef}
        id="about"
        className="relative w-full bg-[#050505] py-16 md:py-32 px-4 sm:px-6 md:px-12 overflow-hidden"
      >
        {/* Ghost watermark */}
        <div className="about-watermark absolute top-8 left-0 select-none">ABOUT</div>

        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(212,175,55,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.03) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 lg:gap-24 items-center">

            {/* LEFT — IMAGE */}
            <div className={`reveal ${visible ? "shown" : ""} relative`}>
              <div
                className="relative overflow-hidden"
                style={{ clipPath: "polygon(0 0, 94% 0, 100% 6%, 100% 100%, 6% 100%, 0 94%)" }}
              >
                <img
                  src={showroom}
                  alt="Sri Kalikambal Autos Showroom"
                  className="w-full h-auto object-cover block"
                  style={{ aspectRatio: "4/3" }}
                />
                <div className="img-overlay absolute inset-0 pointer-events-none" />
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-[#D4AF37]"
                  style={{ boxShadow: "0 0 20px rgba(253,80,80,0.5)" }}
                />
              </div>

              <div className="corner-bracket tl" />
              <div className="corner-bracket tr" />
              <div className="corner-bracket bl" />
              <div className="corner-bracket br" />

              {/* Years badge */}
              <div className="hex-badge absolute -bottom-5 -right-5 w-24 h-24 bg-[#D4AF37] flex flex-col items-center justify-center z-10 shadow-lg" style={{ boxShadow: "0 0 30px rgba(212,175,55,0.4)" }}>
                <strong className="font-bebas text-[28px] text-white leading-none">20+</strong>
                <span className="font-barlow-cond text-[9px] tracking-[2px] uppercase text-white/80">Years</span>
              </div>

              {/* Stats row */}
              <div className="flex gap-6 mt-10">
                {stats.map((s) => (
                  <div key={s.label} className="flex-1">
                    <div className="stat-bar h-px mb-3" />
                    <span className="font-bebas text-[32px] text-[#D4AF37] leading-none block">{s.num}</span>
                    <span className="font-barlow-cond text-[10px] tracking-[2px] uppercase text-[#666]">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — CONTENT */}
            <div className="flex flex-col gap-6">

              {/* Eyebrow */}
              <div className={`reveal delay-100 ${visible ? "shown" : ""} flex items-center gap-3 font-barlow-cond text-[11px] tracking-[4px] uppercase text-[#D4AF37]`}>
                Our Story
                <span className="w-10 h-px bg-[#D4AF37]" />
              </div>

              {/* Heading */}
              <h2
                className={`reveal delay-200 ${visible ? "shown" : ""} font-bebas leading-[1]`}
                style={{ fontSize: "clamp(34px, 8vw, 80px)" }}
              >
                <span className="text-[#D4AF37]">Kalikambal</span><br />
                Autos
              </h2>

              {/* Body paragraphs — 16px, #9CA3AF (much more readable) */}
              <p className={`reveal delay-200 ${visible ? "shown" : ""} font-barlow text-[#9CA3AF] text-[16px] leading-[1.85]`}>
                Your trusted destination for premium motorcycles in Katpadi. We proudly offer the latest models with expert guidance and a customer-first approach that's earned us over two decades of loyalty.
              </p>
              <p className={`reveal delay-200 ${visible ? "shown" : ""} font-barlow text-[#9CA3AF] text-[16px] leading-[1.85]`}>
                Whether you crave performance, style, or everyday comfort — we match you with the perfect ride, backed by seamless financing and fast delivery.
              </p>

              {/* Features grid */}
              <div className={`reveal delay-300 ${visible ? "shown" : ""} grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2`}>
                {features.map((f) => (
                  <div
                    key={f.label}
                    className="feat-card flex items-center gap-3 bg-[#111111] px-4 py-4"
                  >
                    <div className="w-10 h-10 flex-shrink-0 bg-[#D4AF37]/12 flex items-center justify-center text-[18px] text-[#D4AF37]">
                      {f.icon}
                    </div>
                    <div>
                      {/* Feature label — bumped to 15px, brighter */}
                      <div className="font-barlow font-600 text-[15px] text-[#e8e8e0]">{f.label}</div>
                      {/* Sub label — bumped to 13px, slightly brighter */}
                      <div className="font-barlow text-[13px] text-[#777]">{f.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className={`reveal delay-400 ${visible ? "shown" : ""} flex flex-wrap gap-4 mt-2`}>
                <button onClick={onOpenModal} className="btn-slash flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] text-white pl-6 pr-4 py-3 font-barlow-cond text-[13px] font-bold tracking-[2px] uppercase transition-colors duration-300">
                  Explore Bikes
                  <div className="w-8 h-8 bg-black/20 flex items-center justify-center text-base"><FaArrowRight /></div>
                </button>
                <button className="font-barlow-cond text-[13px] tracking-[2px] uppercase text-[#9CA3AF] border border-[#D4AF37]/25 px-6 py-3 hover:border-[#D4AF37] hover:text-[#F5F5F5] transition-all duration-300">
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;